import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import TextEditor from "../../Componets/TextEditor";
import { Mic, ChevronUp, ChevronDown, HelpCircle } from "lucide-react";
import { FaMicrophoneLines } from "react-icons/fa6";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import imageCompression from "browser-image-compression";
import { useSpeechToText } from "../utilities/SpeechToText";
import { Toaster, toast } from "sonner";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Link } from "react-router-dom";
import i18next from "i18next";
import Logo from "../../Images/Logonew.jpeg";
import { authGoogle } from "../utilities/Authentication";
import { statusFailure } from "../Constant";
import Context from "../../Context";
import { CustomLanguageDropdown } from "../../Componets/CustomLanguageDropdown";
import PageHeader from "../../components/WriteComponents/PageHeader";
import { renderHtmlContent } from "../utilities/CommonFunction";
import CustomSelect from "../../components/WriteComponents/CustomSelect";
import SindhiFontWoff2 from "../../fonts/SHREE-DEV7-0714.woff2";
import SindhiFontWoff from "../../fonts/SHREE-DEV7-0714.woff";

/**
 * Read-only review field for Page 3.
 */
function ReviewField({ label, value, placeholder = "—", isSindhi = false }) {
  return (
    <div className="review-field">
      <span className="review-field-label">{label}</span>
      <span
        className="review-field-value"
        style={isSindhi ? { fontFamily: "SHREE-DEV7-0714" } : undefined}
      >
        {value || placeholder}
      </span>
    </div>
  );
}

const SindhiFontStyles = createGlobalStyle`
  @font-face {
    font-family: 'SHREE-DEV7-0714';
    src:
      url(${SindhiFontWoff2}) format('woff2'),
      url(${SindhiFontWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

/**
 * Core shared write component for poems + stories (regular + contest).
 *
 * This first version focuses on:
 * - Regular submissions (no contest extras yet)
 * - Shared fields: title, category, duration, language, description, thumbnail
 * - Draft save + submit hooks
 *
 * Contest-specific fields and stricter validation will be added in subsequent steps.
 */

function WriteContent({
  mode, // "poem" | "story"
  isContest = false,
  initialForm,
  initialPage = 1,
  contestData = null,
  amount = null,
  apis,
  navigation,
}) {
  const { t } = useTranslation();

  const {
    createContent,
    createDraft,
    updateDraft,
    getCategories,
    getLanguages,
    uploadThumbImage,
    uploadIDCardsImage,
  } = apis;

  const { onDraftSaved, onSubmitSuccess } = navigation;

  const [form, setForm] = useState({
    name: "",
    thumbnail: "",
    categoryId: [],
    description: "",
    duration: "",
    language: "",
    contestId: initialForm?.contestId || "",
    id: initialForm?.id || "",
    isAdultContent: initialForm?.isAdultContent || false,
    ...initialForm,
  });

  // 3-page flow UI state (display-only; does not change data structure)
  const [currentPage, setCurrentPage] = useState(
    initialPage >= 1 && initialPage <= 3 ? initialPage : 1,
  );

  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [error, setError] = useState("");
  const [btnStatus, setBtnStatus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [image, setImage] = useState(null);
  const [cropper, setCropper] = useState(null);
  const [base64Image, setBase64Image] = useState("");
  const [imgdata, setImgData] = useState("");
  const [croppedImageData, setCroppedImageData] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const idCardFileInputRef = useRef(null);

  // Contest-specific image state (ID card)
  const [idCardImage, setIdCardImage] = useState(null);
  const [idCardCropper, setIdCardCropper] = useState(null);
  const [idCardBase64, setIdCardBase64] = useState("");
  const [idCardSelectedFile, setIdCardSelectedFile] = useState(null);

  // Contest-specific How to Pay modal
  const [payOpen, setPayOpen] = useState(false);

  // Contest-only login popup
  const { dispatch } = useContext(Context);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Validation error field (for red border highlight)
  const [validationErrorField, setValidationErrorField] = useState(null);

  // Speech to text
  const selectedLanguage =
    languages && languages.length > 0
      ? languages.find((l) => l.name === form.language)
      : null;

  const selectedLanguageLabel = selectedLanguage
    ? selectedLanguage.label
    : "English";

  const {
    isRecording: speechIsRecording,
    transcript: speechTranscript,
    interimTranscript: speechInterimTranscript,
    startRecording: speechStartRecording,
    stopRecording: speechStopRecording,
  } = useSpeechToText(undefined, selectedLanguageLabel);

  const isPoem = mode === "poem";
  const isSindhiLanguage = (form.language || "").toLowerCase() === "sindhi";

  // Derived flags for contest behavior
  const noReview = isContest && contestData?.noReview === true;
  const showDuration = !isContest || !contestData || contestData.durationRequired !== false;

  // Debug: log incoming initialForm and internal form
  useEffect(() => {
    console.log("[WriteContent] initialForm prop changed:", initialForm);
  }, [initialForm]);

  // When initialForm changes (e.g., draft loaded), sync it into local form state
  useEffect(() => {
    if (!initialForm) return;
    console.log("[WriteContent] syncing form from initialForm");
    setForm((prev) => ({
      ...prev,
      ...initialForm,
    }));
  }, [initialForm]);

  // Contest auth: show login popup if not authenticated
  useEffect(() => {
    if (!isContest) return;

    const checkAuth = () => {
      const token = localStorage.getItem("loginDetails");
      setShowLoginPopup(!token);
    };

    checkAuth();
    window.addEventListener("storage", checkAuth);
    window.addEventListener("logout", checkAuth);

    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("logout", checkAuth);
    };
  }, [isContest]);

  const handleGoogleSuccess = (credentialResponse) => {
    if (!credentialResponse.credential) return;
    const tokenId = credentialResponse.credential;

    authGoogle({ token: tokenId })
      .then((response) => {
        if (response.status !== statusFailure) {
          localStorage.setItem("loginDetails", response.data.token);
          dispatch({ type: "login", payload: response.data.token });
          setShowLoginPopup(false);
          toast.success(
            response.message || "Logged in successfully with Google.",
          );
        } else {
          toast.error(response.message || "Google login failed.");
        }
      })
      .catch((error) => {
        console.error("Google auth error:", error);
        localStorage.removeItem("loginDetails");
        setShowLoginPopup(true);
        toast.error("Google login failed. Please try again.");
      });
  };

  const handleLanguageDropdownChange = (event) => {
    const selectedLanguage = event.target.value;
    i18next.changeLanguage(selectedLanguage);
  };

  // Fetch categories + languages once
  useEffect(() => {
    let cancelled = false;

    getCategories({ moduleType: 1 })
      .then((response) => {
        if (cancelled) return;
        if (response.status !== "Failure" && response.status !== "failed") {
          setCategories(response.data);
        } else {
          const msg = response.message || "Failed to load categories";
          setError(msg);
          toast.error(msg);
        }
      })
      .catch(() => {
        if (cancelled) return;
        const msg = "Failed to load categories";
        setError(msg);
        toast.error(msg);
      });

    getLanguages()
      .then((response) => {
        if (cancelled) return;

        const langArr = response && (response.data || response);
        if (Array.isArray(langArr) && langArr.length > 0) {
          setLanguages(langArr);
          setForm((prev) => ({
            ...prev,
            language: prev.language || langArr[0]?.name || "",
          }));
        } else {
          setLanguages([]);
        }
      })
      .catch(() => {
        if (cancelled) return;
        const msg = "Failed to load languages";
        setError(msg);
        toast.error(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [getCategories, getLanguages]);

  const handleSpeechToggle = () => {
    if (speechIsRecording) {
      speechStopRecording();
    } else {
      speechStartRecording();
    }
  };

  const onFormChange = (e) => {
    const { id, name, value, checked } = e.target;

    if (id === "isAdultContent") {
      setForm((prev) => ({
        ...prev,
        isAdultContent: checked,
      }));
      return;
    }

    if (id === "name") {
      const words = value.trim().split(/\s+/).filter(Boolean).length;
      if (words > 50) {
        const msg = "Title should be less than or equal to 50 words.";
        setError(msg);
        toast.error(msg);
        setBtnStatus(true);
      } else {
        setError("");
        setBtnStatus(false);
      }
    }

    const fieldMap = {
      name: "title",
      duration: "duration",
      refId: "refId",
      cityName: "cityName",
      collegeName: "collegeName",
      collegeAddress: "collegeAddress",
      mobileNumber: "mobileNumber",
    };
    if (fieldMap[name] === validationErrorField) {
      setValidationErrorField(null);
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateLanguage = (value) => {
    if (!value) return;
    if (validationErrorField === "language") setValidationErrorField(null);
    setForm((prev) => ({
      ...prev,
      language: value,
    }));
  };

  const updateCategory = (id) => {
    if (!id) return;
    if (validationErrorField === "category") setValidationErrorField(null);
    setForm((prev) => ({
      ...prev,
      categoryId: [id],
    }));
  };

  const handleEditorChange = (htmlContent) => {
    if (validationErrorField === "content") setValidationErrorField(null);
    setForm((prev) => ({
      ...prev,
      description: htmlContent,
    }));
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setBase64Image(reader.result);
        setImgData(reader.result);
        resolve(reader.result);
      };
      reader.onerror = reject;
    });
  };

  const convertIdCardToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setIdCardBase64(reader.result);
        resolve(reader.result);
      };
      reader.onerror = reject;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (validationErrorField === "coverImage") setValidationErrorField(null);

    setCroppedImageData("");

    try {
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      await convertToBase64(compressedFile);
      setSelectedFile(compressedFile);
    } catch (err) {
      console.error("Error compressing file:", err);
    }

    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
    // Clear previous thumbnail when user selects a new image
    setForm((prev) => ({ ...prev, thumbnail: "" }));
  };

  const handleIdCardFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (validationErrorField === "idCard") setValidationErrorField(null);

    try {
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);
      await convertIdCardToBase64(compressedFile);
      setIdCardSelectedFile(compressedFile);
    } catch (err) {
      console.error("Error compressing ID card file:", err);
    }

    const reader = new FileReader();
    reader.onload = () => setIdCardImage(reader.result);
    reader.readAsDataURL(file);
  };

  const getCropData = async () => {
    if (!cropper) return;
    try {
      if (validationErrorField === "coverImage") setValidationErrorField(null);
      const canvas = cropper.getCroppedCanvas({
        width: 1024,
        height: 1024,
      });
      const base64Data = canvas.toDataURL("image/jpeg", 0.8);
      const blob = await fetch(base64Data).then((res) => res.blob());

      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };
      const compressedBlob = await imageCompression(blob, options);
      const croppedBase64 = await convertToBase64(compressedBlob);
      setCroppedImageData(croppedBase64);
      setImgData(croppedBase64);
      setImage(base64Data);
    } catch (err) {
      console.error("Error in cropping/uploading image:", err);
      const msg = "Failed to process image. Please try again.";
      setError(msg);
      toast.error(msg);
    }
  };

  const getFilteredLanguages = () => {
    let baseLanguages = languages || [];

    // If contest restricts languages, filter to those
    if (
      isContest &&
      contestData &&
      contestData.languageBasedModel &&
      Array.isArray(contestData.languages) &&
      contestData.languages.length > 0
    ) {
      const allowed = new Set(contestData.languages);
      baseLanguages = baseLanguages.filter((l) => allowed.has(l.name));
    }

    return baseLanguages;
  };

  const validateCommon = (currentForm) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = currentForm.description || "";
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    if (!currentForm.name || currentForm.name.trim() === "") {
      return "Title is required";
    }

    if (!textContent || textContent.trim() === "") {
      return isPoem ? "Poem content is required" : "Story content is required";
    }

    if (!currentForm.thumbnail) {
      return "Thumbnail image is required";
    }

    if (!currentForm.categoryId || currentForm.categoryId.length < 1) {
      return "Please select at least one category";
    }

    if (showDuration && (!currentForm.duration || currentForm.duration === "")) {
      return "Duration is required";
    }

    if (!currentForm.language) {
      return "Please select a language";
    }

    return null;
  };

  const validateContestExtras = (currentForm) => {
    if (!isContest || !contestData) return null;

    if (contestData.durationRequired && !currentForm.duration) {
      return { field: "duration", message: "Duration is required" };
    }

    if (contestData.refId) {
      if (!currentForm.refId) {
        return {
          field: "refId",
          message: "Please upload your Payment reference id no.",
        };
      }
      if (currentForm.refId && currentForm.refId.length !== 12) {
        return { field: "refId", message: "Incorrect UPI refId!" };
      }
    }

    if (contestData.idCard && !currentForm.idCard) {
      return { field: "idCard", message: "Please upload idCard image" };
    }

    if (contestData.cityName && !currentForm.cityName) {
      return { field: "cityName", message: "Please enter your city name" };
    }

    if (contestData.collegeName && !currentForm.collegeName) {
      return {
        field: "collegeName",
        message: "Please enter your college name",
      };
    }

    if (contestData.collegeAddress && !currentForm.collegeAddress) {
      return {
        field: "collegeAddress",
        message: "Please enter your college address",
      };
    }

    if (contestData.mobileNumber) {
      if (!currentForm.mobileNumber) {
        return {
          field: "mobileNumber",
          message: "Please enter your mobile number",
        };
      }
      if (
        currentForm.mobileNumber &&
        !/^\d{10}$/.test(currentForm.mobileNumber)
      ) {
        return {
          field: "mobileNumber",
          message: "Mobile number must be exactly 10 digits",
        };
      }
    }

    // Word limit check
    if (contestData.wordLimit != null) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = currentForm.description || "";
      const plainText = tempDiv.textContent || tempDiv.innerText || "";
      const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).filter(Boolean).length : 0;
      if (wordCount > contestData.wordLimit) {
        return {
          field: "content",
          message: `Your submission exceeds the ${contestData.wordLimit}-word limit (you have ${wordCount} words).`,
        };
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setBtnStatus(true);
    setIsSubmitting(true);
    setError("");

    try {
      let submitForm = { ...form };

      // Ensure thumbnail is uploaded - use cropped (croppedImageData) when user cropped, else full (imgdata/selectedFile)
      if (
        !submitForm.thumbnail &&
        (croppedImageData || imgdata || selectedFile)
      ) {
        let imageToUpload = croppedImageData || imgdata;
        if (selectedFile && !croppedImageData && !imgdata) {
          const base64 = await convertToBase64(selectedFile);
          imageToUpload = base64;
        }
        if (imageToUpload) {
          const response = await uploadThumbImage(imageToUpload);
          submitForm.thumbnail = response.location;
          setForm((prev) => ({ ...prev, thumbnail: response.location }));
        }
      }

      // Contest: ensure ID card is uploaded if required
      if (
        isContest &&
        contestData &&
        contestData.idCard &&
        !submitForm.idCard &&
        (idCardBase64 || idCardSelectedFile)
      ) {
        let idCardToUpload = idCardBase64;
        if (idCardSelectedFile && !idCardBase64) {
          const base64 = await convertIdCardToBase64(idCardSelectedFile);
          idCardToUpload = base64;
        }
        if (idCardToUpload) {
          const idResp = await uploadIDCardsImage(idCardToUpload);
          submitForm.idCard = idResp.location;
          setForm((prev) => ({ ...prev, idCard: idResp.location }));
        }
      }

      const validationError = validateCommon(submitForm);
      if (validationError) {
        setError(validationError);
        toast.error(validationError);
        setBtnStatus(false);
        setIsSubmitting(false);
        return;
      }

      const contestValidationError = validateContestExtras(submitForm);
      if (contestValidationError) {
        setError(contestValidationError.message);
        setValidationErrorField(contestValidationError.field);
        toast.error(contestValidationError.message);
        setBtnStatus(false);
        setIsSubmitting(false);
        return;
      }

      // Ensure categoryId is array
      if (typeof submitForm.categoryId === "string") {
        submitForm.categoryId = [submitForm.categoryId];
      }

      const response = await createContent(submitForm);

      if (response.status === "Failure" || response.status === "failed") {
        throw new Error(
          response.message || "Failed to submit. Please try again.",
        );
      }

      toast.success(response.message || "Submitted successfully.");

      if (onSubmitSuccess) {
        onSubmitSuccess({
          isContest,
          response,
          form: submitForm,
        });
      }
    } catch (err) {
      console.error("Submit error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "An unexpected error occurred. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBtnStatus(false);
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setBtnStatus(true);
    setError("");

    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = form.description || "";
      const textContent = tempDiv.textContent || tempDiv.innerText || "";

      if (!textContent || textContent.trim() === "") {
        const msg = isPoem
          ? "Poem content is required to save a draft"
          : "Story content is required to save a draft";
        setValidationErrorField("content");
        setError(msg);
        toast.error(msg);
        setBtnStatus(false);
        return;
      }

      if (!form.name || form.name.trim() === "") {
        if (currentPage === 1) {
          setCurrentPage(2);
          const msg = t("Write.AddTitleToSaveDraft") || "Please add a title to save your draft";
          setValidationErrorField("title");
          setError(msg);
          toast.error(msg);
          setBtnStatus(false);
          return;
        } else {
          const msg = t("Write.writeStory.TitleRequiredDraft") || "Title is required to save a draft";
          setValidationErrorField("title");
          setError(msg);
          toast.error(msg);
          setBtnStatus(false);
          return;
        }
      }

      setValidationErrorField(null);

      let draftForm = { ...form };

      // Upload cover image if not yet uploaded - use cropped (croppedImageData) when user cropped, else full (imgdata/selectedFile)
      if (
        !draftForm.thumbnail &&
        (croppedImageData || imgdata || selectedFile)
      ) {
        let imageToUpload = croppedImageData || imgdata;
        if (selectedFile && !croppedImageData && !imgdata) {
          const base64 = await convertToBase64(selectedFile);
          imageToUpload = base64;
        }
        if (imageToUpload) {
          const response = await uploadThumbImage(imageToUpload);
          draftForm.thumbnail = response.location;
          setForm((prev) => ({ ...prev, thumbnail: response.location }));
        }
      }

      let resp;
      if (draftForm.id) {
        resp = await updateDraft(draftForm.id, draftForm);
      } else {
        resp = await createDraft(draftForm);
      }

      if (resp.status === "Failure" || resp.status === "failed" || !resp.data) {
        throw new Error(resp.message || "Failed to save draft.");
      }

      const draft = resp.data;
      setForm((prev) => ({
        ...prev,
        id: draft._id,
      }));
      toast.success("Draft saved successfully.");

      if (onDraftSaved) {
        onDraftSaved({ isContest, draft });
      }
    } catch (err) {
      console.error("Draft save error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to save draft. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBtnStatus(false);
    }
  };

  const titleLabel = isPoem
    ? t("Write.WritePoem.EnterTitle")
    : t("Write.writeStory.EnterTitle");

  const titlePlaceholder = isPoem
    ? t("Write.WritePoem.innerTitle")
    : t("Write.writeStory.innerTitle");

  const categoryLabel = isPoem
    ? t("Write.WritePoem.Category.Category")
    : t("Write.writeStory.Category.Category");

  const durationLabel = isPoem
    ? t("Write.WritePoem.Duration")
    : t("Write.writeStory.Duration");

  const durationPlaceholder = isPoem
    ? t("Write.WritePoem.innerDuration")
    : t("Write.writeStory.innerDuration");

  const languageLabel = isPoem
    ? t("Write.WritePoem.Language.Language")
    : t("Write.writeStory.Language.Language");

  const writeHeader = isPoem
    ? t("Write.WritePoem.WritePoetry")
    : t("Write.writeStory.WriteStory");

  const editorPlaceholder =
    t("Write.writeStory.WriteDescription") || "Write the description";

  const submitLabel = isPoem
    ? t("Write.WritePoem.Button2")
    : t("Write.writeStory.Button2");

  const availableLanguages = getFilteredLanguages();

  // If contest restricts to a single language and form.language is empty or invalid, default it
  useEffect(() => {
    if (!isContest || !contestData) return;

    // Default category mapping
    if (contestData.categoryControl && contestData.categoryControl !== "user_selects" && contestData.defaultCategory) {
      if (!form.categoryId || form.categoryId[0] !== contestData.defaultCategory) {
        setForm((prev) => ({
          ...prev,
          categoryId: [contestData.defaultCategory],
        }));
      }
    }

    // Default target mapping
    if (contestData.targetRequired === false && contestData.defaultTarget) {
      const isAdult = contestData.defaultTarget === "adult";
      if (form.isAdultContent !== isAdult) {
        setForm((prev) => ({
          ...prev,
          isAdultContent: isAdult,
        }));
      }
    }

    const filtered = getFilteredLanguages();
    if (filtered.length === 1) {
      const onlyLang = filtered[0];
      setForm((prev) => ({
        ...prev,
        language: prev.language || onlyLang.name,
      }));
    } else if (
      filtered.length > 1 &&
      (!form.language || !filtered.some((l) => l.name === form.language))
    ) {
      setForm((prev) => ({
        ...prev,
        language: filtered[0].name,
      }));
    }
  }, [isContest, contestData, languages, form.language]);

  // Honor ?step=setup (initialPage=2) when resuming a draft; draft load updates initialForm async
  useEffect(() => {
    if (initialPage === 2) {
      setCurrentPage(2);
    }
  }, [initialPage, initialForm]);

  const handleClose = () => {
    // No dedicated navigation prop yet; keep it simple and non-invasive.
    window.history.back();
  };

  const validatePage2 = (currentForm) => {
    if (!currentForm.name || currentForm.name.trim() === "") {
      return {
        field: "title",
        message: isPoem
          ? "Please enter a title for your poem"
          : "Please enter a title for your story",
      };
    }
    if (
      !currentForm.thumbnail &&
      !croppedImageData &&
      !imgdata &&
      !selectedFile
    ) {
      return { field: "coverImage", message: "Please upload a cover image" };
    }
    if (!currentForm.categoryId || currentForm.categoryId.length < 1) {
      return { field: "category", message: "Please select a category" };
    }
    if (showDuration && (!currentForm.duration || currentForm.duration === "")) {
      return { field: "duration", message: "Please enter the duration" };
    }
    if (!currentForm.language) {
      return { field: "language", message: "Please select a language" };
    }
    const contestError = validateContestExtras(currentForm);
    if (contestError) return contestError;
    return null;
  };

  const goNext = () => {
    if (currentPage === 1) {
      const plainText = getPlainTextFromHtml(form.description);
      if (!plainText || !plainText.trim()) {
        setValidationErrorField("content");
        toast.error(
          isPoem
            ? "Please write your poem content before proceeding."
            : "Please write your story content before proceeding.",
        );
        return;
      }
      // Word limit check — block leaving the writing page if over the limit
      if (isContest && contestData && contestData.wordLimit != null) {
        const wordCount = plainText.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount > contestData.wordLimit) {
          setValidationErrorField("content");
          toast.error(
            `Your submission exceeds the ${contestData.wordLimit}-word limit (you have ${wordCount} words).`,
          );
          return;
        }
      }
      setValidationErrorField(null);
    }
    if (currentPage === 2) {
      const result = validatePage2(form);
      if (result) {
        setValidationErrorField(result.field);
        toast.error(result.message);
        return;
      }
      setValidationErrorField(null);

      if (noReview) {
        // Skip review — submit directly
        handleSubmit(new Event("submit"));
        return;
      }
    }
    setCurrentPage((p) => Math.min(noReview ? 2 : 3, p + 1));
  };
  const goBack = () => {
    setValidationErrorField(null);
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const getPlainTextFromHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html || "";
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const getWordCount = (html) => {
    const text = getPlainTextFromHtml(html);
    return text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  };

  // Only disable during save - allow click so validation errors show via toast
  const saveDraftDisabled = btnStatus || isSubmitting;

  // Only disable during submission - allow click so validation errors show via toast
  const submitDisabled = btnStatus || isSubmitting;

  return (
    <Root>
      {isSindhiLanguage && <SindhiFontStyles />}
      <Toaster />
      {isContest && showLoginPopup && (
        <PopupOverlay>
          <PopupContent>
            <img className="img" height="80" width="80" src={Logo} alt="logo" />
            <center>
              <p className="event-p3">{t("Login.Welcome")}</p>
            </center>
            <div className="login-container">
              <GoogleOAuthProvider clientId="451080537233-a0gdg7j86p0valvd0vbs161ebeqgq0lt.apps.googleusercontent.com">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => {
                    console.error("Google Login Failed");
                    toast.error("Google login failed. Please try again.");
                  }}
                  useOneTap={false}
                  ux_mode="popup"
                  type="standard"
                  theme="filled_black"
                  text="continue_with"
                  shape="rectangular"
                  locale="en"
                />
              </GoogleOAuthProvider>
            </div>
            <CustomLanguageDropdown onChange={handleLanguageDropdownChange} />

            <div style={{ textAlign: "center", marginTop: "30px" }}>
              <p style={{ color: "white" }}>
                By continuing, you agree to our <br />
                <Link to="/term-condition" className="text-blue underline mx-1">
                  terms and conditions
                </Link>
                and
                <Link to="/privacy-policy" className="text-blue underline mx-1">
                  privacy policy
                </Link>
                .
              </p>
            </div>
          </PopupContent>
        </PopupOverlay>
      )}

      <Card>
        <PageHeader
          currentPage={currentPage}
          totalPages={noReview ? 2 : 3}
          onClose={handleClose}
          onSaveDraft={handleSaveDraft}
          saveDraftDisabled={saveDraftDisabled}
          saveDraftLabel={t("Write.writeStory.SaveAsDraft") || "SAVE AS DRAFT"}
          showSaveDraft={true}
        />
        <CardContent>
          {currentPage === 1 && (
            <>
              <PageTitleBlock>
                <PageTitle
                  style={
                    isSindhiLanguage
                      ? { fontFamily: "SHREE-DEV7-0714" }
                      : undefined
                  }
                >
                  {(form.name || "").trim()
                    ? form.name.trim()
                    : isPoem
                      ? t("Write.WritePoem.UntitledPoem") || "Untitled Poem"
                      : t("Write.writeStory.UntitledStory") || "Untitled Story"}
                </PageTitle>
                <PageSubtitle>
                  {isPoem
                    ? t("Write.WritePoem.WritePoemBelow") ||
                      "Write your poem content below."
                    : t("Write.writeStory.WriteStoryBelow") ||
                      "Write your story below."}
                </PageSubtitle>
              </PageTitleBlock>
              <div className="story-write-container">
                <div className="input-container-story-write">
                  <TextEditor
                    initialValue={form.description}
                    placeholder={editorPlaceholder}
                    onChange={handleEditorChange}
                    speechTranscript={speechTranscript}
                    interimTranscript={speechInterimTranscript}
                    isRecording={speechIsRecording}
                    hasValidationError={validationErrorField === "content"}
                    contentFontFamily={
                      isSindhiLanguage
                        ? 'SHREE-DEV7-0714, "Roboto", sans-serif'
                        : undefined
                    }
                  />
                </div>
              </div>
              <div className="page-2-toolbar">
                <div className="word-count">
                  {getWordCount(form.description)}{" "}
                  {getWordCount(form.description) === 1
                    ? t("Write.Word") || "word"
                    : t("Write.Words") || "words"}
                  {isContest && contestData && contestData.wordLimit != null && (
                    <span
                      style={{
                        color: getWordCount(form.description) > contestData.wordLimit ? "#e53e3e" : "inherit",
                        marginLeft: "4px",
                      }}
                    >
                      {`/ ${contestData.wordLimit}`}
                    </span>
                  )}
                </div>
                <div className="speech-controls">
                  <span className="speech-label">
                    {t("Write.SpeechToText") || "SPEECH TO TEXT"}
                  </span>
                  <button
                    type="button"
                    onClick={handleSpeechToggle}
                    className={`speech-button ${
                      speechIsRecording ? "recording" : ""
                    }`}
                  >
                    {speechIsRecording ? (
                      <FaMicrophoneLines size={16} />
                    ) : (
                      <Mic size={16} />
                    )}
                  </button>
                </div>
              </div>
              <Separator />
              <div className="uploader-buttons" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={goNext}
                  className="button primary-btn"
                >
                  {t("Write.ContinueToSetup") || "Continue to Setup →"}
                </button>
              </div>
            </>
          )}

          {currentPage === 2 && (
            <div
              style={{
                width: "100%",
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <PageTitleBlock>
                <PageTitle>
                  {isPoem
                    ? t("Write.WritePoem.PoemSetup") || "Poem Setup"
                    : t("Write.writeStory.StorySetup") || "Story Setup"}
                </PageTitle>
                <PageSubtitle>
                  {isPoem
                    ? t("Write.WritePoem.PoemSetupSubtitle") ||
                      "Complete your poem details. Use a title that captures the essence."
                    : t("Write.writeStory.StorySetupSubtitle") ||
                      "Define the soul of your story."}
                </PageSubtitle>
              </PageTitleBlock>
              <div className="create-story-container">
                <div
                  className={`input-container ${
                    validationErrorField === "title" ? "validation-error" : ""
                  }`}
                >
                  <input
                    onChange={onFormChange}
                    name="name"
                    id="name"
                    type="text"
                    value={form.name}
                    required
                    placeholder={
                      isPoem
                        ? t("Write.WritePoem.EnterTitlePlaceholder") ||
                          titlePlaceholder
                        : t("Write.writeStory.EnterTitlePlaceholder") ||
                          "Enter your story title here..."
                    }
                    className="input input-title-underline"
                    style={
                      isSindhiLanguage
                        ? { fontFamily: "SHREE-DEV7-0714" }
                        : undefined
                    }
                  />
                </div>
              </div>

              <div className="uploader-outer-container">
                <div
                  className={`uploader-container ${
                    validationErrorField === "coverImage"
                      ? "validation-error"
                      : ""
                  }`}
                >
                  <form id="file-upload-form" className="uploader">
                    <input
                      ref={fileInputRef}
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      name="thumbnail"
                      style={{ display: "none" }}
                    />

                    {image || form.thumbnail ? (
                      <div
                        id="image-container"
                        className="cropper-wrapper"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Cropper
                          key={image || form.thumbnail}
                          style={{
                            height: "100%",
                            width: "100%",
                          }}
                          initialAspectRatio={1}
                          preview=".img-preview"
                          src={image || form.thumbnail}
                          viewMode={1}
                          minCropBoxHeight={10}
                          minCropBoxWidth={10}
                          background={false}
                          responsive={true}
                          autoCropArea={1}
                          checkOrientation={false}
                          onInitialized={(instance) => {
                            setCropper(instance);
                          }}
                          guides={true}
                          dragMode="move"
                        />
                      </div>
                    ) : (
                      <label htmlFor="file-upload" id="file-drag">
                        <div id="image-container">
                          {base64Image ? (
                            <img
                              id="file-image"
                              src={base64Image}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <img
                              src="/images/download-image.svg"
                              alt=""
                              className="upload-placeholder-icon"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "120px",
                                width: "56px",
                                height: "56px",
                              }}
                            />
                          )}
                        </div>
                        <div id="text-container">
                          <div
                            id="start"
                            className={base64Image ? "hidden" : ""}
                          >
                            <i
                              className="fa fa-download"
                              aria-hidden="true"
                            ></i>
                            <div>
                              <div
                                className="upload-text"
                                style={{
                                  fontWeight: 800,
                                  fontSize: "16px",
                                  color: "var(--color-text-secondary)",
                                }}
                              >
                                {t("Write.writeStory.AddCoverArtwork") ||
                                  "ADD COVER ARTWORK"}
                              </div>
                            </div>
                            <div id="notimage" className="hidden">
                              {t("SelectImage") || "Please select an image"}
                            </div>
                          </div>
                        </div>
                      </label>
                    )}
                  </form>
                </div>

                {(image || form.thumbnail) && (
                  <div
                    className="cropper-buttons"
                    style={{
                      marginBottom: "10px",
                      marginTop: "10px",
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={getCropData}
                      className="button uploader-action-btn"
                    >
                      {t("Write.writeStory.CropImage") || "Crop Image"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                          fileInputRef.current.click();
                        }
                      }}
                      className="button uploader-action-btn"
                    >
                      {t("Write.writeStory.SelectImage") || "Select Image"}
                    </button>
                  </div>
                )}
              </div>

              {isContest && contestData && contestData.idCard && (
                <div className="uploader-outer-container">
                  <div
                    className={`uploader-container ${
                      validationErrorField === "idCard"
                        ? "validation-error"
                        : ""
                    }`}
                  >
                    <form id="idcard-upload-form" className="uploader">
                      <input
                        ref={idCardFileInputRef}
                        id="idcard-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleIdCardFileChange}
                        name="idCard"
                        style={{ display: "none" }}
                      />
                      {idCardImage ? (
                        <div
                          id="image-container"
                          className="cropper-wrapper"
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Cropper
                            key={idCardImage}
                            style={{
                              height: "100%",
                              width: "100%",
                            }}
                            initialAspectRatio={1}
                            preview=".img-preview"
                            src={idCardImage}
                            viewMode={1}
                            minCropBoxHeight={10}
                            minCropBoxWidth={10}
                            background={false}
                            responsive={true}
                            autoCropArea={1}
                            checkOrientation={false}
                            onInitialized={(instance) => {
                              setIdCardCropper(instance);
                            }}
                            guides={true}
                            dragMode="move"
                          />
                        </div>
                      ) : (
                        <label
                          htmlFor="idcard-upload"
                          id="file-drag"
                          className="uploader-empty-state"
                        >
                          <div id="image-container">
                            <img
                              src="/images/download-image.svg"
                              alt=""
                              className="upload-placeholder-icon"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "120px",
                                width: "56px",
                                height: "56px",
                              }}
                            />
                          </div>
                          <div id="text-container">
                            <div
                              id="start"
                              className={idCardBase64 ? "hidden" : ""}
                            >
                              <i
                                className="fa fa-download"
                                aria-hidden="true"
                              />
                              <div>
                                <div
                                  className="upload-text"
                                  style={{
                                    fontWeight: 800,
                                    fontSize: "16px",
                                    color: "var(--color-text-secondary)",
                                  }}
                                >
                                  {t("Write.writeStory.uploadIdCard") ||
                                    "UPLOAD ID CARD"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </label>
                      )}
                    </form>
                  </div>
                  {idCardImage && (
                    <div
                      className="cropper-buttons"
                      style={{
                        marginBottom: "10px",
                        marginTop: "10px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={async () => {
                          if (!idCardCropper) return;
                          try {
                            const canvas = idCardCropper.getCroppedCanvas({
                              width: 1024,
                              height: 1024,
                            });
                            const base64Data = canvas.toDataURL(
                              "image/jpeg",
                              0.8,
                            );
                            setIdCardBase64(base64Data);
                            setIdCardImage(base64Data);
                          } catch (err) {
                            console.error("Error cropping ID card:", err);
                          }
                        }}
                        className="button uploader-action-btn"
                      >
                        {t("Write.writeStory.CropImage") || "Crop Image"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (idCardFileInputRef.current) {
                            idCardFileInputRef.current.value = "";
                            idCardFileInputRef.current.click();
                          }
                        }}
                        className="button uploader-action-btn"
                      >
                        {t("Write.writeStory.SelectImage") || "Select Image"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="create-story-container">
                <div className="input-row input-row-metadata">
                  {(!isContest || contestData?.categoryControl !== "hidden") && (
                  <div
                    className={`metadata-field ${
                      validationErrorField === "category"
                        ? "validation-error"
                        : ""
                    }`}
                  >
                    <CustomSelect
                      label={categoryLabel?.toUpperCase() || "CATEGORY"}
                      value={form.categoryId?.[0] || ""}
                      onChange={(next) => updateCategory(next)}
                      placeholderLabel={
                        t("Write.writeStory.SelectCategory") ||
                        "Select a category"
                      }
                      options={(categories || []).map((category) => ({
                        value: category._id,
                        label: category.name,
                      }))}
                      disabled={isContest && contestData?.categoryControl === "read_only"}
                    />
                  </div>
                  )}

                  {showDuration && (
                  <div
                    className={`metadata-field ${
                      validationErrorField === "duration"
                        ? "validation-error"
                        : ""
                    }`}
                  >
                    <div className="metadata-field-box metadata-duration-wrapper">
                      <h1 className="label label-uppercase metadata-field-label">
                        {durationLabel?.toUpperCase() || "DURATION"}
                      </h1>
                      <div className="metadata-duration-input-row">
                        <input
                          placeholder={durationPlaceholder}
                          onChange={onFormChange}
                          name="duration"
                          type="number"
                          value={form.duration}
                          required
                          className="input-select metadata-select metadata-duration-input"
                          min={1}
                        />
                        <div className="duration-arrows">
                          <button
                            type="button"
                            className="duration-arrow-btn"
                            onClick={() => {
                              const current = Number(form.duration) || 0;
                              setForm((prev) => ({
                                ...prev,
                                duration: String(current + 1),
                              }));
                            }}
                            aria-label="Increment duration"
                          >
                            <ChevronUp size={12} />
                          </button>
                          <button
                            type="button"
                            className="duration-arrow-btn"
                            onClick={() => {
                              const current = Number(form.duration) || 1;
                              if (current > 1) {
                                setForm((prev) => ({
                                  ...prev,
                                  duration: String(current - 1),
                                }));
                              }
                            }}
                            aria-label="Decrement duration"
                            disabled={Number(form.duration) <= 1}
                          >
                            <ChevronDown size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  <div
                    className={`metadata-field ${
                      validationErrorField === "language"
                        ? "validation-error"
                        : ""
                    }`}
                  >
                    <CustomSelect
                      label={languageLabel?.toUpperCase() || "LANGUAGE"}
                      value={form.language || ""}
                      onChange={(next) => updateLanguage(next)}
                      options={(availableLanguages || []).map((language) => ({
                        value: language.name,
                        label: language.label,
                      }))}
                    />
                  </div>

                  {(!isContest || contestData?.targetRequired !== false) && (
                  <div className="metadata-field">
                    <CustomSelect
                      label={t("Write.writeStory.Target") || "TARGET"}
                      value={form.isAdultContent ? "adult" : "everyone"}
                      onChange={(next) => {
                        setForm((prev) => ({
                          ...prev,
                          isAdultContent: next === "adult",
                        }));
                      }}
                      options={[
                        {
                          value: "everyone",
                          label: t("Write.writeStory.Everyone") || "Everyone",
                        },
                        {
                          value: "adult",
                          label: t("Write.writeStory.Adult") || "Adults",
                        },
                      ]}
                    />
                  </div>
                  )}
                </div>
              </div>

              {isContest && contestData && (
                <div className="create-story-container">
                  <div className="input-row input-row-contest">
                    {contestData.refId && (
                      <div
                        className={`metadata-field ${
                          validationErrorField === "refId"
                            ? "validation-error"
                            : ""
                        }`}
                      >
                        <div className="metadata-field-box">
                          <h1 className="label label-uppercase metadata-field-label metadata-field-label-row">
                            {t("Write.writeStory.UPI-Id") || "UPI REFERENCE ID"}
                            <button
                              type="button"
                              className="help-icon-trigger"
                              onClick={() => setPayOpen(true)}
                              aria-label={
                                t("Write.writeStory.HowtoPay") || "How to Pay"
                              }
                            >
                              <HelpCircle size={14} />
                            </button>
                          </h1>
                          <input
                            placeholder={
                              t("Write.writeStory.Enter-your-upi-id") ||
                              "Enter your UPI reference id"
                            }
                            onChange={onFormChange}
                            name="refId"
                            id="refId"
                            type="text"
                            value={form.refId || ""}
                            className="input-select metadata-select"
                          />
                        </div>
                      </div>
                    )}
                    {contestData.cityName && (
                      <div
                        className={`metadata-field ${
                          validationErrorField === "cityName"
                            ? "validation-error"
                            : ""
                        }`}
                      >
                        <div className="metadata-field-box">
                          <h1 className="label label-uppercase metadata-field-label">
                            {t("Write.writeStory.CityName") || "CITY NAME"}
                          </h1>
                          <input
                            placeholder={
                              t("Write.writeStory.EnterCityName") ||
                              "Enter your city"
                            }
                            onChange={onFormChange}
                            name="cityName"
                            id="cityName"
                            type="text"
                            value={form.cityName || ""}
                            className="input-select metadata-select"
                          />
                        </div>
                      </div>
                    )}
                    {contestData.collegeName && (
                      <div
                        className={`metadata-field ${
                          validationErrorField === "collegeName"
                            ? "validation-error"
                            : ""
                        }`}
                      >
                        <div className="metadata-field-box">
                          <h1 className="label label-uppercase metadata-field-label">
                            {t("Write.writeStory.CollegeName") ||
                              "COLLEGE NAME"}
                          </h1>
                          <input
                            placeholder={
                              t("Write.writeStory.EnterCollegeName") ||
                              "Enter your college name"
                            }
                            onChange={onFormChange}
                            name="collegeName"
                            id="collegeName"
                            type="text"
                            value={form.collegeName || ""}
                            className="input-select metadata-select"
                          />
                        </div>
                      </div>
                    )}
                    {contestData.collegeAddress && (
                      <div
                        className={`metadata-field ${
                          validationErrorField === "collegeAddress"
                            ? "validation-error"
                            : ""
                        }`}
                      >
                        <div className="metadata-field-box">
                          <h1 className="label label-uppercase metadata-field-label">
                            {t("Write.writeStory.CollegeAddress") ||
                              "COLLEGE ADDRESS"}
                          </h1>
                          <input
                            placeholder={
                              t("Write.writeStory.EnterCollegeAddress") ||
                              "Enter your college address"
                            }
                            onChange={onFormChange}
                            name="collegeAddress"
                            id="collegeAddress"
                            type="text"
                            value={form.collegeAddress || ""}
                            className="input-select metadata-select"
                          />
                        </div>
                      </div>
                    )}
                    {contestData.mobileNumber && (
                      <div
                        className={`metadata-field ${
                          validationErrorField === "mobileNumber"
                            ? "validation-error"
                            : ""
                        }`}
                      >
                        <div className="metadata-field-box">
                          <h1 className="label label-uppercase metadata-field-label">
                            {t("Write.writeStory.MobileNumber") ||
                              "MOBILE NUMBER"}
                          </h1>
                          <input
                            placeholder={
                              t("Write.writeStory.MobileNumberPlaceholder") ||
                              "ex: XXXXXXXXXX"
                            }
                            onChange={onFormChange}
                            name="mobileNumber"
                            id="mobileNumber"
                            type="text"
                            value={form.mobileNumber || ""}
                            className="input-select metadata-select"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Separator />
              <div className="uploader-buttons" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={goBack}
                  className="button secondary-btn"
                >
                  {t("Write.Back") || "Back"}
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="button primary-btn"
                >
                  {noReview
                    ? (isPoem
                        ? t("Write.WritePoem.FinishPublishPoem") || "Finish & Publish Poem"
                        : t("Write.writeStory.FinishPublishStory") || "Finish & Publish Story")
                    : (isPoem
                        ? t("Write.WritePoem.ReviewPoem") || "Review Poem"
                        : t("Write.writeStory.ReviewStory") || "Review Story")}
                </button>
              </div>
            </div>
          )}

          {currentPage === 3 && (
            <>
              <PageTitleBlock>
                <PageTitle>
                  {isPoem
                    ? t("Write.WritePoem.ReviewPoem") || "Review Poem"
                    : t("Write.writeStory.ReviewStory") || "Review Story"}
                </PageTitle>
                <PageSubtitle>
                  {t("Write.writeStory.CheckBeforePublish") ||
                    "Check everything before publishing."}
                </PageSubtitle>
              </PageTitleBlock>

              <div className="review-section">
                <h2 className="review-section-label">
                  {t("Write.writeStory.ReviewDetails") || "REVIEW DETAILS"}
                </h2>
                <div className="review-fields">
                  <div className="review-field-full">
                    <ReviewField
                      label={t("Write.Title")?.toUpperCase() || "TITLE"}
                      value={form.name?.trim() || ""}
                      placeholder={t("Write.Untitled") || "Untitled"}
                      isSindhi={isSindhiLanguage}
                    />
                  </div>
                  <div className="review-grid">
                    <ReviewField
                      label={categoryLabel?.toUpperCase() || "CATEGORY"}
                      value={
                        categories?.find((c) => c._id === form.categoryId?.[0])
                          ?.name || ""
                      }
                      placeholder="—"
                    />
                    {showDuration && (
                    <ReviewField
                      label={durationLabel?.toUpperCase() || "DURATION"}
                      value={
                        form.duration
                          ? `${form.duration} ${t("Write.writeStory.MinRead") || "min read"}`
                          : ""
                      }
                      placeholder="—"
                    />
                    )}
                    <ReviewField
                      label={languageLabel?.toUpperCase() || "LANGUAGE"}
                      value={
                        languages?.find((l) => l.name === form.language)
                          ?.label ||
                        form.language ||
                        ""
                      }
                      placeholder="—"
                    />
                    <ReviewField
                      label={
                        t("Write.writeStory.Target")?.toUpperCase() || "TARGET"
                      }
                      value={
                        form.isAdultContent
                          ? t("Write.writeStory.Adult") || "Adult"
                          : t("Write.writeStory.Everyone") || "Everyone"
                      }
                      placeholder="—"
                    />
                  </div>
                </div>
                <div className="review-thumbnail">
                  <span className="review-field-label">
                    {t("Write.writeStory.CoverArtwork") || "COVER ARTWORK"}
                  </span>
                  {croppedImageData || form.thumbnail || imgdata ? (
                    <img
                      src={croppedImageData || form.thumbnail || imgdata}
                      alt="Cover"
                      className="review-thumbnail-img"
                    />
                  ) : (
                    <span
                      className="review-field-value"
                      style={{ opacity: 0.6 }}
                    >
                      —
                    </span>
                  )}
                </div>
                {isContest && contestData && (
                  <div className="review-grid review-contest-fields">
                    {contestData.refId && (
                      <ReviewField
                        label={
                          t("Write.writeStory.UPIReferenceId") ||
                          "UPI REFERENCE ID"
                        }
                        value={form.refId || ""}
                        placeholder="—"
                      />
                    )}
                    {contestData.cityName && (
                      <ReviewField
                        label={t("Write.writeStory.City") || "CITY"}
                        value={form.cityName || ""}
                        placeholder="—"
                      />
                    )}
                    {contestData.collegeName && (
                      <ReviewField
                        label={
                          t("Write.writeStory.CollegeNameLabel") ||
                          "COLLEGE NAME"
                        }
                        value={form.collegeName || ""}
                        placeholder="—"
                      />
                    )}
                    {contestData.collegeAddress && (
                      <ReviewField
                        label={
                          t("Write.writeStory.CollegeAddressLabel") ||
                          "COLLEGE ADDRESS"
                        }
                        value={form.collegeAddress || ""}
                        placeholder="—"
                      />
                    )}
                    {contestData.mobileNumber && (
                      <ReviewField
                        label={
                          t("Write.writeStory.MobileNumber") || "MOBILE NUMBER"
                        }
                        value={form.mobileNumber || ""}
                        placeholder="—"
                      />
                    )}
                    {contestData.idCard && (
                      <div className="review-id-card">
                        <span className="review-field-label">
                          {t("Write.writeStory.IDCard") || "ID CARD"}
                        </span>
                        {form.idCard ? (
                          <img
                            src={form.idCard}
                            alt="ID Card"
                            className="review-id-card-img"
                          />
                        ) : (
                          <span
                            className="review-field-value"
                            style={{ opacity: 0.6 }}
                          >
                            —
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="review-section">
                <h2 className="review-section-label">
                  {t("Write.writeStory.ContentSnippet") || "CONTENT SNIPPET"}
                </h2>
                <div
                  className={`review-content-snippet${
                    isSindhiLanguage ? " sindhi-font" : ""
                  }`}
                >
                  {getPlainTextFromHtml(form.description).trim() ? (
                    <div className="review-content-html">
                      {renderHtmlContent(form.description)}
                    </div>
                  ) : (
                    t("Write.writeStory.NoContentWritten") ||
                    "No content written."
                  )}
                </div>
              </div>

              <Separator />
              <div className="review-actions">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitDisabled}
                  className="button primary-btn review-submit-btn"
                >
                  {btnStatus || isSubmitting ? (
                    <span className="loader" />
                  ) : (
                    <span>
                      {isPoem
                        ? t("Write.WritePoem.FinishPublishPoem") ||
                          "Finish & Publish Poem"
                        : t("Write.writeStory.FinishPublishStory") ||
                          "Finish & Publish Story"}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={goBack}
                  className="review-back-link"
                >
                  {t("Write.writeStory.GoBackToWriting") ||
                    "Go Back to Writing"}
                </button>
              </div>
            </>
          )}

          {isContest && contestData?.refId && payOpen && (
            <PayModalOverlay>
              <PayModalContainer>
                <PayCloseButton onClick={() => setPayOpen(false)}>
                  ✕
                </PayCloseButton>
                <h3>{t("Pay.HowToPay") || "How to Pay"}</h3>

                <StepSection color="#e6f2ff">
                  <h4>{t("Pay.step1") || "Step 1"}</h4>
                  <p>
                    {t("Pay.step1Desc") ||
                      "Scan the QR code below and pay the contest fee."}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      className="payqr"
                      src="https://story-tent-testing.s3.ap-south-1.amazonaws.com/contest-pay-qr.jpeg"
                      alt="Payment QR Code"
                    />
                  </div>
                </StepSection>

                <StepSection color="#e6f9f0">
                  <h4>{t("Pay.step2") || "Step 2"}</h4>
                  <p>
                    {t("Pay.step2Desc") ||
                      "Copy the UPI reference ID from your payment app and paste it in the field above."}
                  </p>
                </StepSection>

                <StepSection
                  color="#fff2f2"
                  style={{
                    borderColor: "red",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      marginBottom: 0,
                      fontSize: "20px",
                      textAlign: "center",
                    }}
                  >
                    {t("Pay.disclaimer") ||
                      "Please double-check your UPI reference ID before submitting."}
                  </p>
                  <p style={{ marginBottom: 0, textAlign: "center" }}>
                    {t("Pay.disc1") ||
                      "Incorrect or invalid reference IDs may lead to disqualification."}
                  </p>
                  <p style={{ marginBottom: 0, textAlign: "center" }}>
                    {t("Pay.disc2") || "Contest fees are non-refundable."}
                  </p>
                </StepSection>
              </PayModalContainer>
            </PayModalOverlay>
          )}
        </CardContent>
      </Card>
    </Root>
  );
}

export default WriteContent;

const Root = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: transparent;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  font-family: "Roboto", sans-serif;
  padding: 20px;
  padding-bottom: 40px;

  @media (max-width: 768px) {
    padding: 10px;
    padding-bottom: 10px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 900px;
  background-color: var(--color-surface, #ffffff);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const PageTitleBlock = styled.div`
  width: 100%;
  max-width: 100%;
  margin-bottom: 5px;
  text-align: left;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 5px 0;
  font-family: "Lora", serif;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
  font-family: "Lora", serif;
  margin-bottom: 16px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--color-border);
  margin: 8px 0;
`;

const CardContent = styled.div`
  width: 100%;
  padding: 0 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .create-story-container {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;
  }

  /* Reduce gap between metadata and contest sections */
  .create-story-container:has(.input-row-metadata) {
    margin-bottom: 0;
  }

  .create-story-container:has(.input-row-contest) {
    margin-top: 0;
    padding-top: 0;
  }

  .input-container {
    width: 100%;
    max-width: 800px;
  }

  .input-row {
    display: flex;
    gap: 20px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex-wrap: wrap;
  }

  .input-row.input-row-metadata {
    margin-top: 16px;
    margin-bottom: 16px;

    @media (max-width: 767px) {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
      min-width: 0;
      margin-top: 10px;
      margin-bottom: 10px;
    }

    @media (min-width: 768px) {
      flex-wrap: nowrap;
      gap: 26px;
    }
  }

  /* Contest fields: max 4 per row, equal width */
  .input-row.input-row-contest {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px 26px;
    margin-top: 0;
    margin-bottom: 16px;
    width: 100%;
    min-width: 0;

    .metadata-field {
      min-width: 0;
    }

    @media (max-width: 767px) {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
      margin-top: 0;
      margin-bottom: 10px;
    }
  }

  .metadata-field-label-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .help-icon-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    padding: 0;
    border-radius: 50%;
    background-color: var(--color-tertiary, #f4f5f1);
    color: var(--color-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    transition:
      background-color 0.2s,
      border-color 0.2s;

    &:hover {
      background-color: color-mix(
        in srgb,
        var(--color-border) 30%,
        var(--color-tertiary, #f4f5f1)
      );
      border-color: var(--color-text-secondary);
    }
  }

  /* Mobile: 1st full, 2nd+3rd half (2nd full if alone), 4th full, 5th+6th half (5th full if alone) */
  @media (max-width: 767px) {
    .input-row-metadata .metadata-field:nth-child(1) {
      grid-column: 1 / -1;
    }
    .input-row-metadata .metadata-field:nth-child(2):last-child {
      grid-column: 1 / -1;
    }
    .input-row-metadata .metadata-field:nth-child(2):not(:last-child) {
      grid-column: 1;
    }
    .input-row-metadata .metadata-field:nth-child(3) {
      grid-column: 2;
    }
    .input-row-metadata .metadata-field:nth-child(4) {
      grid-column: 1 / -1;
    }
    .input-row-metadata .metadata-field:nth-child(5):last-child {
      grid-column: 1 / -1;
    }
    .input-row-metadata .metadata-field:nth-child(5):not(:last-child) {
      grid-column: 1;
    }
    .input-row-metadata .metadata-field:nth-child(6) {
      grid-column: 2;
    }
  }

  .input-container-select,
  .input-container-duration,
  .input-container-select-lang {
    flex: 1;
    min-width: 200px;

    @media (min-width: 768px) {
      min-width: 0;
    }
  }

  .metadata-field {
    flex: 1;
    min-width: 200px;

    @media (max-width: 767px) {
      min-width: 0;
      overflow: hidden;
    }

    @media (min-width: 768px) {
      min-width: 0;
    }
  }

  .metadata-field-box {
    background-color: var(--color-tertiary, #f4f5f1);
    border: 1px solid var(--color-border);
    border-color: color-mix(
      in srgb,
      var(--color-border) 45%,
      var(--color-surface)
    );
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

    @media (max-width: 767px) {
      background: transparent;
      border: none;
      border-radius: 0;
      padding: 0;
      min-width: 0;
      width: 100%;
    }
  }

  /* Higher specificity to override .label and .label-uppercase on metadata fields */
  .metadata-field-box .metadata-field-label {
    margin: 0;
    font-family: "Roboto", sans-serif;
    font-size: 10px;
    font-weight: 750;
    line-height: 1;
    letter-spacing: 0.02em;
    color: var(--color-text-secondary);
    opacity: 0.65;
    padding-left: 10px;
    padding-top: 8px;
    padding-bottom: 6px;

    @media (max-width: 767px) {
      padding: 0;
      margin-bottom: 6px;
    }
  }

  /* Mobile: select/input gets box styling (label appears outside) */
  @media (max-width: 767px) {
    .metadata-field-box select.metadata-select,
    .metadata-field-box input.metadata-select {
      border: 1px solid
        color-mix(in srgb, var(--color-border) 45%, var(--color-surface));
      border-radius: 12px;
      background-color: var(--color-tertiary, #f4f5f1);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      min-width: 0;
      width: 100%;
      box-sizing: border-box;
    }
    /* Duration: box wraps input+arrows row */
    .metadata-duration-wrapper .metadata-duration-input-row {
      border: 1px solid
        color-mix(in srgb, var(--color-border) 45%, var(--color-surface));
      border-radius: 12px;
      background-color: var(--color-tertiary, #f4f5f1);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      min-width: 0;
    }
    .metadata-duration-wrapper .metadata-duration-input {
      border: none;
      background: transparent;
      box-shadow: none;
    }
  }

  /* Very narrow screens (340px+): tighter gap and padding so fields fit */
  @media (max-width: 400px) {
    .input-row.input-row-metadata {
      gap: 8px;
    }
    .metadata-field-box select.metadata-select {
      padding-right: 28px;
      background-position: right 8px center;
    }
    .metadata-duration-wrapper .metadata-duration-input {
      padding-right: 28px;
    }
    .duration-arrows {
      right: 4px;
    }
  }

  .metadata-duration-input-row {
    position: relative;
  }

  .label {
    font-size: 12px;
    font-family: "Roboto", sans-serif;
    font-weight: 300;
    color: var(--color-text);
  }

  .input,
  .input-select {
    width: 100%;
    background-color: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    font-size: 9px;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
    }
  }

  /* Override for metadata fields - must come after .input-select */
  .metadata-field-box select.metadata-select,
  .metadata-field-box input.metadata-select {
    height: 40px;
    min-height: 40px;
    box-sizing: border-box;
    border: none;
    border-radius: 0;
    background-color: var(--color-tertiary, #f4f5f1);
    padding: 8px 14px 6px 14px;
    font-size: 16px;
    font-family: "Roboto", sans-serif;
    font-weight: 600;
    color: var(--color-text);
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23465C4C' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 38px;
  }

  .metadata-field-box input.metadata-select {
    background-image: none;
    padding-right: 12px;
    -moz-appearance: textfield;
  }

  .metadata-field-box input.metadata-select::-webkit-outer-spin-button,
  .metadata-field-box input.metadata-select::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .metadata-duration-wrapper .metadata-duration-input {
    padding-right: 32px;
  }

  .duration-arrows {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 0;
    pointer-events: none;
  }

  .duration-arrow-btn {
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--color-text);
    cursor: pointer;
    line-height: 0;

    &:hover:not(:disabled) {
      color: var(--color-primary);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .input-title-underline {
    border: none;
    border-bottom: 1px solid var(--color-border);
    border-radius: 0;
    background-color: transparent;
    padding-left: 0;
    padding-right: 0;
    padding-top: 6px;
    padding-bottom: 10px;
    font-size: 44px;
    line-height: 1.1;
    font-family: "Lora", serif;
    font-weight: 600;
    color: var(--color-text);

    &::placeholder {
      color: var(--color-text-secondary);
      font-weight: 600;
      font-size: 34px;
      opacity: 0.28;
    }

    @media (max-width: 768px) {
      font-size: 28px;

      &::placeholder {
        font-size: 28px;
      }
    }
  }

  .validation-error {
    &.input-container input,
    &.input-container .input-title-underline {
      border-bottom-color: var(--color-error) !important;
    }
    &.uploader-container {
      border-color: var(--color-error) !important;
    }
    &.metadata-field .metadata-field-box,
    &.metadata-field .metadata-duration-input-row {
      border-color: var(--color-error) !important;
    }
    &.input-container-duration input {
      border-color: var(--color-error) !important;
    }
  }

  .story-write-container {
    width: 100%;
    display: flex;
    gap: 30px;
    align-items: flex-start;

    @media (max-width: 900px) {
      flex-direction: column;
    }
  }

  .input-container-story-write {
    flex: 2;
    width: 100%;

    .RichTextEditor__root,
    .story-writing-area {
      border-radius: 16px !important;
    }
    .RichTextEditor__toolbar {
      border-top-left-radius: 16px !important;
      border-top-right-radius: 16px !important;
    }
    .RichTextEditor__editor {
      border-bottom-left-radius: 16px !important;
      border-bottom-right-radius: 16px !important;
    }
  }

  .story-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    h1 {
      font-family: "Lora", serif;
      font-size: 26px;
      color: var(--color-primary);
      font-weight: 700;
    }
  }

  .page-2-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
    width: 100%;
    padding: 12px 16px;
    border-radius: 16px;
  }

  .word-count {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-text);
    opacity: 0.8;
  }

  .review-section {
    width: 100%;
    margin-bottom: 24px;
  }

  .review-section-label {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: var(--color-text);
    opacity: 0.8;
    margin: 0 0 12px 0;
  }

  .review-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .review-field-full {
    width: 100%;
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 16px;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .review-contest-fields {
    margin-top: 16px;
  }

  .review-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px;
    background-color: var(--color-tertiary, #f4f5f1);
    border-radius: 16px;
    border: 1px solid var(--color-border);
  }

  .review-field-label {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 300;
    letter-spacing: 0.05em;
    color: var(--color-text);
    opacity: 0.7;
  }

  .review-field-value {
    font-family: "Roboto", sans-serif;
    font-size: 15px;
    font-weight: 400;
    color: var(--color-text);
  }

  .review-thumbnail {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .review-thumbnail-img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: 16px;
    border: 1px solid var(--color-border);
  }

  .review-id-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .review-id-card-img {
    max-width: 100%;
    max-height: 150px;
    object-fit: contain;
    border-radius: 16px;
    border: 1px solid var(--color-border);
  }

  .review-content-snippet {
    font-family: "Roboto", sans-serif;
    padding: 16px;
    background: var(--color-background, #f8f8f8);
    border: 1px solid var(--color-border);
    border-radius: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: var(--color-text-tertiary);
    min-height: 80px;
    max-height: 300px;
    overflow-y: auto;
    word-break: break-word;
  }

  .review-content-snippet .review-content-html {
    color: var(--color-text-tertiary);
    line-height: 1.6;
  }

  .review-content-snippet .review-content-html p,
  .review-content-snippet .review-content-html strong,
  .review-content-snippet .review-content-html em,
  .review-content-snippet .review-content-html u {
    color: inherit;
  }

  .review-content-snippet .review-content-html p {
    margin: 0 0 0.5em 0;
  }

  .review-content-snippet .review-content-html p:last-child {
    margin-bottom: 0;
  }

  /* When Sindhi is selected, force the Sindhi font on the snippet
     container and all inner text elements, overriding Itim from about.css */
  .review-content-snippet.sindhi-font,
  .review-content-snippet.sindhi-font .review-content-html,
  .review-content-snippet.sindhi-font .review-content-html p,
  .review-content-snippet.sindhi-font .review-content-html strong,
  .review-content-snippet.sindhi-font .review-content-html em,
  .review-content-snippet.sindhi-font .review-content-html u {
    font-family: "SHREE-DEV7-0714", "Roboto", sans-serif;
  }

  .review-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
  }

  .review-submit-btn {
    width: 100%;
    max-width: 320px;
  }

  .review-back-link {
    font-family: "Roboto", sans-serif;
    background: none;
    border: none;
    color: var(--color-text);
    opacity: 0.8;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;

    &:hover {
      opacity: 1;
    }
  }

  .uploader-outer-container {
    width: 100%;
    max-width: 100%;
    margin-bottom: 10px;
  }

  .uploader-container {
    width: 100%;
    height: 200px;
    border: 2px dashed var(--color-border);
    border-radius: 12px;
    background-color: var(--color-surface);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin-bottom: 20px;
    transition: all 0.2s;

    &:hover {
      border-color: var(--color-text);
    }

    /* Remove cropper grey/dark background when image is selected */
    .cropper-modal {
      background: transparent !important;
      opacity: 0 !important;
    }

    .cropper-bg {
      background: transparent !important;
    }

    .cropper-container {
      background: transparent !important;
    }
  }

  .uploader {
    width: 100%;
    height: 100%;
  }

  #file-drag {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 20px;
    color: var(--color-text-secondary);
  }

  .upload-placeholder-icon {
    opacity: 0.2;
    transition:
      opacity 0.2s ease,
      color 0.2s ease;
  }

  #file-drag:hover .upload-placeholder-icon {
    opacity: 0.9;
    color: var(--color-text);
  }

  .upload-text {
    font-family: "Roboto", sans-serif;
    color: var(--color-text-secondary);
    margin-top: 10px;
    font-size: 12px;
    font-weight: 400;
    text-align: center;
  }

  .uploader-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .uploader-action-btn {
    background-color: transparent;
    border: 1px solid var(--color-border);
    color: var(--color-text);
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    min-height: 36px;
    min-width: auto;
    flex: none;
    box-shadow: none;

    &:hover {
      background-color: var(--color-tertiary, #f4f5f1);
      border-color: var(--color-text);
    }
  }

  .uploader-buttons {
    display: flex;
    gap: 10px;
    width: 100%;
    justify-content: flex-end;
    margin-top: 8px;

    .button {
      flex: none;
      min-width: auto;
    }

    @media (max-width: 767px) {
      .button {
        flex: 1;
        width: 100%;
      }
    }
  }

  .label-uppercase {
    font-family: "Roboto", sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 12px;
    font-weight: 300;
    color: var(--color-text-secondary);
  }

  .button {
    flex: 1;
    padding: 12px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-family: "Roboto", sans-serif;
    font-size: 16px;
    font-weight: 400;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .primary-btn {
    background-color: var(--color-text);
    color: var(--color-surface);
    &:hover {
      opacity: 0.9;
    }
  }

  .secondary-btn {
    background-color: transparent;
    border: 1px solid var(--color-text);
    color: var(--color-text);
    &:hover {
      background-color: var(--color-text);
      color: var(--color-surface);
    }
  }

  .action-btn {
    background-color: var(--color-primary);
    color: var(--color-text-inverse);
    width: auto;
    min-width: 160px;
    font-family: "Roboto", sans-serif;
    font-size: 16px;
    font-weight: 400;
    flex: none;
  }

  .speech-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    background-color: var(--color-sttGreen);
    padding: 10px;
    border-radius: 20px;
  }

  .speech-label {
    font-family: "Roboto", sans-serif;
    color: var(--color-text);
    font-size: 12px;
    font-weight: 500;
  }

  .speech-button {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-text);
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background-color: var(--color-background);
      border-color: var(--color-text);
    }

    &.recording {
      background-color: #ef4444;
      color: white;
      border-color: #ef4444;
      animation: pulse 1.5s infinite;
    }
  }

  .loader {
    animation: spin 2s linear infinite;
    width: 16px;
    height: 16px;
    border: 2px solid var(--color-border);
    border-top-color: var(--color-text);
    border-radius: 50%;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  .pop-up,
  .pop-up-success {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--color-surface);
    border: 1px solid var(--color-border);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 15px 40px 15px 20px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    z-index: 1000;
    min-width: 300px;
  }

  .pop-up {
    border-left: 4px solid #ef4444;
  }

  .pop-up-success {
    border-left: 4px solid #22c55e;
  }

  .popup-content {
    font-family: "Roboto", sans-serif;
    display: flex;
    align-items: center;
    color: var(--color-text);
    font-size: 14px;
  }
`;

const PayModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;

  .payqr {
    width: 200px;
    height: 250px;
  }
`;

const PayModalContainer = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  position: relative;
  border: 1px solid var(--color-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  h3,
  h4,
  p {
    color: var(--color-text);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 22px;
    font-weight: 700;
  }
`;

const PayCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-size: 20px;
  cursor: pointer;
  color: var(--color-text);
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const StepSection = styled.div`
  background-color: ${(props) => props.color || "#f0f0f0"};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 1px solid var(--color-border);
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const PopupContent = styled.div`
  background-color: #0f1817;
  padding: 20px;
  border-radius: 14px;
  width: 90%;
  height: auto;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h2 {
    color: white;
    text-align: center;
    margin-bottom: 20px;
    font-size: 24px;
  }

  .login-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
`;
